import React from "react";
import styled from "styled-components";
import moment from "moment";

import {
  Button,
  Card,
  SectionHeader,
  Skeleton,
} from "../../../../design-system";
import { AnalysisData, ANALYSIS_ROUTINE } from "./analysisData";
import { StatGrid, StatCard } from "./ui";

// The day's call, and the numbers the routine chose to put beside it.
//
// This file used to be the whole Analysis tab: verdict, tiles, a rail table, a
// funnel and the findings list. The rail table moved to RailTable, which merged
// it with the capacity bars it duplicated; the findings moved into the work
// queue, because a finding IS work; the funnel moved to the results card. What
// is left is the one thing a reader should see first: what today was.

const HeaderControls = styled.div`
  display: flex;
  align-items: center;
  gap: var(--space-3);
  flex-wrap: wrap;
`;

const DaySelect = styled.select`
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  color: var(--color-text);
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  padding: var(--space-2) var(--space-3);
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid var(--color-accent);
    outline-offset: 1px;
  }
`;

const Verdict = styled.div<{ $tone: string }>`
  margin-top: var(--space-5);
  padding: var(--space-4);
  border-radius: var(--radius-md);
  border: 1px solid
    ${(p) =>
      p.$tone === "crit"
        ? "var(--color-danger)"
        : p.$tone === "warn"
        ? "var(--color-warning)"
        : "var(--color-success)"};
  background: var(--color-surface-inset);
`;

const VerdictHeadline = styled.div`
  font-family: var(--font-sans);
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text);
  line-height: var(--line-height-snug);
`;

const VerdictDetail = styled.p`
  margin: var(--space-2) 0 0;
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  line-height: 1.55;
  color: var(--color-text-secondary);
`;

// The routine does not always run before the reader arrives, so the newest
// analysis is often about yesterday. Saying so is not a nicety: without it a
// stale "the drip recovered" reads as today's news.
const Stale = styled.p`
  margin: var(--space-3) 0 0;
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  color: var(--color-warning);
`;

const Message = styled.p`
  margin: var(--space-4) 0 0;
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  line-height: 1.55;
  color: var(--color-text-secondary);
`;

const ErrorText = styled(Message)`
  color: var(--color-danger);
`;

const Footer = styled.div`
  margin-top: var(--space-5);
  padding-top: var(--space-4);
  border-top: 1px solid var(--color-border-subtle);
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
  color: var(--color-text-tertiary);
  line-height: 1.6;
`;

export type DailyAnalysisProps = {
  analysis: AnalysisData;
  today: string;
  onOpenHistory: () => void;
};

const DailyAnalysis: React.FC<DailyAnalysisProps> = ({
  analysis,
  today,
  onOpenHistory,
}) => {
  const {
    analyses,
    current,
    error,
    selectedDate,
    setSelectedDate,
    latestDate,
  } = analysis;

  const header = (
    <SectionHeader
      title="Today"
      description="The day's read of the pipeline, written after the send runs finish."
      actions={
        <HeaderControls>
          {analyses && analyses.length > 1 && (
            <DaySelect
              value={current?.date ?? selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              aria-label="Analysis day"
            >
              {analyses.map((a) => (
                <option key={a.date} value={a.date}>
                  {a.date}
                </option>
              ))}
            </DaySelect>
          )}
          <Button variant="ghost" size="sm" onClick={onOpenHistory}>
            History
          </Button>
        </HeaderControls>
      }
    />
  );

  if (error) {
    return (
      <Card padding="lg" elevation="md">
        {header}
        <ErrorText>{error}</ErrorText>
      </Card>
    );
  }

  if (!analyses) {
    return (
      <Card padding="lg" elevation="md">
        {header}
        <div style={{ marginTop: "var(--space-5)" }}>
          <Skeleton width="100%" height={120} />
        </div>
      </Card>
    );
  }

  if (!current) {
    return (
      <Card padding="lg" elevation="md">
        {header}
        <Message>
          No analysis posted yet. One is written each day by {ANALYSIS_ROUTINE},
          after the send runs finish.
        </Message>
      </Card>
    );
  }

  // A day older than the newest is the reader's own choice, so it is labelled
  // plainly rather than warned about.
  const viewingOlder = !!latestDate && current.date !== latestDate;
  const daysBehind = moment
    .utc(today, "YYYY-MM-DD")
    .diff(moment.utc(current.date, "YYYY-MM-DD"), "days");

  return (
    <Card padding="lg" elevation="md">
      {header}

      {current.verdict && (
        <Verdict $tone={current.verdict.tone}>
          <VerdictHeadline>{current.verdict.headline}</VerdictHeadline>
          {current.verdict.detail && (
            <VerdictDetail>{current.verdict.detail}</VerdictDetail>
          )}
        </Verdict>
      )}

      {daysBehind > 0 && (
        <Stale>
          {viewingOlder
            ? `You are reading ${current.date} UTC. The newest is ${latestDate}.`
            : `This is the newest analysis and it is about ${current.date} UTC, ${
                daysBehind === 1 ? "a day" : `${daysBehind} days`
              } ago. Today has not been read yet.`}
        </Stale>
      )}

      {(current.tiles ?? []).length > 0 && (
        <div style={{ marginTop: "var(--space-5)" }}>
          <StatGrid>
            {(current.tiles ?? []).map((tile, i) => (
              <StatCard
                key={`${tile.label}-${i}`}
                label={tile.label}
                value={tile.value}
                hint={tile.detail}
              />
            ))}
          </StatGrid>
        </div>
      )}

      <Footer>
        {current.generatedAt
          ? `Written ${moment(current.generatedAt).fromNow()}`
          : "Written by the daily analysis routine"}
        {current.source ? ` by ${current.source}` : ""}
        {current.sources && current.sources.length > 0
          ? `. Read: ${current.sources.join(", ")}.`
          : "."}
      </Footer>
    </Card>
  );
};

export default DailyAnalysis;
